import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Check,
  Download,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Settings2,
  SlidersHorizontal,
  Users,
  X,
} from 'lucide-react'
import { api, getApiErrorMessage } from '../lib/api'
import { EmptyState, ErrorState } from '../components/ui/feedback-state'
import { PageFrame, PageHeader } from '../ui/PageLayout'

const CATEGORY_ORDER = ['CONTACT', 'COVERAGE', 'OTHER']

function unwrap(response) {
  const body = response?.data
  return body && typeof body === 'object' && Object.prototype.hasOwnProperty.call(body, 'data')
    ? body.data
    : body
}

function money(value) {
  const amount = Number(value || 0)
  if (!Number.isFinite(amount)) return '0원'
  if (amount >= 10000 && amount % 10000 === 0) return `${(amount / 10000).toLocaleString()}만원`
  return `${amount.toLocaleString()}원`
}

function deepClone(value) {
  return JSON.parse(JSON.stringify(value || []))
}

function findRule(categories, ruleCode) {
  for (const category of categories || []) {
    const rule = (category.rules || []).find((item) => item.code === ruleCode)
    if (rule) return rule
  }
  return null
}

function enabledCount(category) {
  return (category?.rules || []).reduce((sum, rule) => sum + (rule.enabled ? Number(rule.count || 0) : 0), 0)
}

export default function CustomerWorkHubPage() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [activeCategory, setActiveCategory] = useState('CONTACT')
  const [activeRuleCode, setActiveRuleCode] = useState('')
  const [customerData, setCustomerData] = useState(null)
  const [customerLoading, setCustomerLoading] = useState(false)
  const [customerError, setCustomerError] = useState('')
  const [exporting, setExporting] = useState(false)
  const [page, setPage] = useState(0)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [settingsCategory, setSettingsCategory] = useState('CONTACT')
  const [draftCategories, setDraftCategories] = useState([])
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [weeklyData, setWeeklyData] = useState({ topics: [] })
  const [weeklyLoading, setWeeklyLoading] = useState(true)
  const [weeklyError, setWeeklyError] = useState('')
  const [activeWeeklyTopicId, setActiveWeeklyTopicId] = useState(null)
  const [weeklyCustomerData, setWeeklyCustomerData] = useState(null)
  const [weeklyPage, setWeeklyPage] = useState(0)

  const categories = useMemo(() => {
    const raw = Array.isArray(data?.categories) ? data.categories : []
    return [...raw].sort((a, b) => CATEGORY_ORDER.indexOf(a.key) - CATEGORY_ORDER.indexOf(b.key))
  }, [data])
  const weeklyTopics = useMemo(() => Array.isArray(weeklyData?.topics) ? weeklyData.topics : [], [weeklyData])
  const activeWeeklyTopic = weeklyTopics.find((topic) => topic.id === activeWeeklyTopicId) || weeklyTopics[0]

  const category = activeCategory === 'RECOMMENDATION' ? null : categories.find((item) => item.key === activeCategory) || categories[0]
  const visibleRules = useMemo(() => {
    const rules = category?.rules || []
    return activeCategory === 'COVERAGE' ? rules.filter((rule) => rule.enabled) : rules
  }, [category, activeCategory])
  const activeRule = findRule(categories, activeRuleCode)

  const loadOverview = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const response = await api.get('/customer-management/work-hub', { __silent: true })
      setData(unwrap(response) || {})
    } catch (e) {
      setError(getApiErrorMessage(e, '고객업무 허브를 불러오지 못했습니다.'))
    } finally {
      setLoading(false)
    }
  }, [])

  const loadWeekly = useCallback(async () => {
    setWeeklyLoading(true)
    setWeeklyError('')
    try {
      const response = await api.get('/customer-management/work-hub/weekly', {
        params: { includeHistory: true, limit: 50 },
        __silent: true,
      })
      const next = unwrap(response) || { topics: [] }
      setWeeklyData(next)
      const topics = Array.isArray(next.topics) ? next.topics : []
      setActiveWeeklyTopicId((current) => topics.some((topic) => topic.id === current) ? current : topics[0]?.id || null)
      if (!topics.length) setWeeklyCustomerData(null)
    } catch (e) {
      setWeeklyData({ topics: [] })
      setActiveWeeklyTopicId(null)
      setWeeklyCustomerData(null)
      if (e?.response?.status !== 404) {
        setWeeklyError(getApiErrorMessage(e, '추천 주제를 불러오지 못했습니다.'))
      }
    } finally {
      setWeeklyLoading(false)
    }
  }, [])

  const loadWeeklyCustomers = useCallback(async (topicId, targetPage = 0) => {
    if (!topicId) return
    setWeeklyLoading(true)
    setWeeklyError('')
    try {
      const response = await api.get('/customer-management/work-hub/weekly/customers', {
        params: { topicId, page: targetPage, size: 30 },
        __silent: true,
      })
      setWeeklyCustomerData(unwrap(response) || {})
    } catch (e) {
      setWeeklyError(getApiErrorMessage(e, '추천 고객 목록을 불러오지 못했습니다.'))
    } finally {
      setWeeklyLoading(false)
    }
  }, [])
  const loadCustomers = useCallback(async (ruleCode, targetPage = 0) => {
    if (!ruleCode) return
    setCustomerLoading(true)
    setCustomerError('')
    try {
      const response = await api.get('/customer-management/work-hub/customers', {
        params: { ruleCode, page: targetPage, size: 30 },
        __silent: true,
      })
      setCustomerData(unwrap(response) || {})
    } catch (e) {
      setCustomerError(getApiErrorMessage(e, '대상 고객을 불러오지 못했습니다.'))
    } finally {
      setCustomerLoading(false)
    }
  }, [])

  useEffect(() => {
    loadOverview()
    loadWeekly()
  }, [loadOverview, loadWeekly])

  useEffect(() => {
    if (activeCategory !== 'RECOMMENDATION' || !activeWeeklyTopicId) {
      setWeeklyCustomerData(null)
      return
    }
    loadWeeklyCustomers(activeWeeklyTopicId, weeklyPage)
  }, [activeCategory, activeWeeklyTopicId, weeklyPage, loadWeeklyCustomers])

  useEffect(() => {
    if (!category) return
    if (!visibleRules.some((rule) => rule.code === activeRuleCode)) {
      const next = visibleRules.find((rule) => rule.enabled) || visibleRules[0]
      setActiveRuleCode(next?.code || '')
      setPage(0)
    }
  }, [category, visibleRules, activeRuleCode])

  useEffect(() => {
    if (!activeRuleCode) return
    loadCustomers(activeRuleCode, page)
  }, [activeRuleCode, page, loadCustomers])

  const openSettings = () => {
    setDraftCategories(deepClone(categories))
    setSettingsCategory(activeCategory)
    setSaveError('')
    setSettingsOpen(true)
  }

  const saveSettings = async () => {
    setSaving(true)
    setSaveError('')
    try {
      const rules = draftCategories.flatMap((item) => (item.rules || []).map((rule) => ({
        ruleCode: rule.code,
        enabled: Boolean(rule.enabled),
        config: rule.config || {},
      })))
      await api.put('/customer-management/work-hub/preferences', { rules })
      setSettingsOpen(false)
      setPage(0)
      await loadOverview()
      if (activeRuleCode) await loadCustomers(activeRuleCode, 0)
    } catch (e) {
      setSaveError(getApiErrorMessage(e, '고객업무 기준을 저장하지 못했습니다.'))
    } finally {
      setSaving(false)
    }
  }

  const selectCategory = (key) => {
    setActiveCategory(key)
    setPage(0)
  }

  const selectRule = (code) => {
    setActiveRuleCode(code)
    setPage(0)
  }

  const downloadCustomers = async () => {
    if (!activeRuleCode || exporting) return
    setExporting(true)
    setCustomerError('')
    try {
      const response = await api.get('/customer-management/work-hub/customers/export', {
        params: { ruleCode: activeRuleCode },
        responseType: 'blob',
        __silent: true,
      })
      const blob = response.data instanceof Blob ? response.data : new Blob([response.data])
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = (activeRule?.label || 'customers') + '.xlsx'
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
    } catch (e) {
      setCustomerError(getApiErrorMessage(e, '엑셀 파일을 내려받지 못했습니다.'))
    } finally {
      setExporting(false)
    }
  }

  return (
    <PageFrame>
      <PageHeader
        title="고객업무 허브"
        actions={(
          <div className="flex items-center gap-2">
            <Link
              to="/support/customers"
              className="inline-flex h-10 items-center gap-2 rounded-md border border-sgez-border-strong bg-white px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              <Users className="h-4 w-4" aria-hidden="true" />
              고객관리
            </Link>
            <button
              type="button"
              title="기준 설정"
              aria-label="기준 설정"
              onClick={openSettings}
              className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-sgez-border-strong bg-white text-slate-700 hover:bg-slate-50"
            >
              <Settings2 className="h-4 w-4" aria-hidden="true" />
            </button>
            <button
              type="button"
              title="새로고침"
              aria-label="새로고침"
              onClick={async () => {
                await Promise.all([loadOverview(), loadWeekly()])
                if (activeRuleCode) await loadCustomers(activeRuleCode, page)
              }}
              disabled={loading}
              className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-sgez-border-strong bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />
            </button>
          </div>
        )}
      />

      {error ? (
        <ErrorState description={error} onAction={loadOverview} className="mb-4 border border-rose-200 bg-white py-6" />
      ) : null}

      <div className="mb-4 flex overflow-x-auto border-b border-sgez-border-strong" role="tablist" aria-label="업무 카테고리">
        {[...categories, { key: 'RECOMMENDATION', label: '추천' }].map((item) => {
          const selected = item.key === activeCategory
          return (
            <button
              key={item.key}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => selectCategory(item.key)}
              className={`min-w-[112px] border-b-2 px-5 py-3 text-sm font-bold transition ${
                selected
                  ? 'border-brand-navy text-brand-navy'
                  : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              {item.label}
              <span className={`ml-2 tabular-nums ${selected ? 'text-brand-sky' : 'text-slate-400'}`}>
                {item.key === 'RECOMMENDATION' ? Number(activeWeeklyTopic?.total || 0) : enabledCount(item)}
              </span>
            </button>
          )
        })}
      </div>

      {activeCategory === 'RECOMMENDATION' ? (
        <WeeklyRecommendationSection
          data={weeklyData}
          loading={weeklyLoading}
          error={weeklyError}
          activeTopicId={activeWeeklyTopicId}
          customerData={weeklyCustomerData}
          page={weeklyPage}
          onSelect={(topicId) => { setActiveWeeklyTopicId(topicId); setWeeklyPage(0) }}
          onPageChange={setWeeklyPage}
          onRetry={async () => { await loadWeekly(); if (activeWeeklyTopicId) await loadWeeklyCustomers(activeWeeklyTopicId, weeklyPage) }}
        />
      ) : (
        <div className="grid min-h-[540px] gap-4 lg:grid-cols-[340px_minmax(0,1fr)]">
        <section className="overflow-hidden border border-sgez-border-strong bg-white">
          <div className="flex h-12 items-center justify-between border-b border-sgez-border bg-sgez-surface-subtle px-4">
            <h2 className="text-sm font-bold text-slate-900">{category?.label || '업무'} 기준</h2>
            <button
              type="button"
              onClick={openSettings}
              className="inline-flex h-8 items-center gap-1.5 rounded-md px-2 text-xs font-semibold text-slate-600 hover:bg-slate-100"
            >
              <SlidersHorizontal className="h-3.5 w-3.5" aria-hidden="true" />
              설정
            </button>
          </div>
          {loading ? (
            <div className="px-4 py-12 text-center text-sm text-slate-500">불러오는 중...</div>
          ) : (
            <div className="divide-y divide-sgez-border">
              {visibleRules.map((rule) => {
                const selected = rule.code === activeRuleCode
                return (
                  <button
                    key={rule.code}
                    type="button"
                    onClick={() => selectRule(rule.code)}
                    className={`flex w-full items-center gap-3 px-4 py-3 text-left transition ${
                      selected ? 'bg-sky-50' : 'hover:bg-slate-50'
                    }`}
                  >
                    <span className={`h-2 w-2 shrink-0 rounded-full ${rule.enabled ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                    <span className="min-w-0 flex-1">
                      <span className={`block truncate text-sm font-bold ${selected ? 'text-brand-navy' : 'text-slate-800'}`}>
                        {rule.label}
                      </span>
                      <span className="mt-0.5 block truncate text-xs text-slate-500">
                        {rule.enabled ? rule.criterionText : '사용 안 함'}
                      </span>
                    </span>
                    <span className={`min-w-8 text-right text-lg font-black tabular-nums ${rule.enabled ? 'text-slate-900' : 'text-slate-300'}`}>
                      {rule.enabled ? rule.count || 0 : '-'}
                    </span>
                    <ChevronRight className={`h-4 w-4 shrink-0 ${selected ? 'text-brand-sky' : 'text-slate-300'}`} aria-hidden="true" />
                  </button>
                )
              })}
            </div>
          )}
        </section>

        <section className="min-w-0 border border-sgez-border-strong bg-white">
          <div className="flex min-h-16 flex-wrap items-center justify-between gap-3 border-b border-sgez-border bg-sgez-surface-subtle px-4 py-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h2 className="truncate text-base font-black text-slate-900">{activeRule?.label || '대상 고객'}</h2>
                <span className="text-sm font-bold tabular-nums text-brand-sky">{customerData?.total ?? activeRule?.count ?? 0}명</span>
              </div>
              <div className="mt-0.5 truncate text-xs text-slate-500">{activeRule?.criterionText || ''}</div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-slate-500">{data?.date || ''}</span>
              <button
                type="button"
                onClick={downloadCustomers}
                disabled={!activeRule?.enabled || exporting}
                className="inline-flex h-9 items-center gap-1.5 rounded-md border border-sgez-border-strong bg-white px-3 text-xs font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                <Download className="h-4 w-4" aria-hidden="true" />
                {exporting ? '다운로드 중' : '엑셀'}
              </button>
            </div>
          </div>

          {activeRule?.category === 'COVERAGE' && data?.coveragePolicy?.notice ? (
            <div className="border-b border-amber-200 bg-amber-50 px-4 py-2 text-xs font-medium text-amber-900">
              {data.coveragePolicy.notice}
            </div>
          ) : null}

          {customerError ? (
            <ErrorState description={customerError} onAction={() => loadCustomers(activeRuleCode, page)} className="py-10" />
          ) : customerLoading ? (
            <div className="px-4 py-16 text-center text-sm text-slate-500">대상 고객을 확인하는 중...</div>
          ) : !activeRule?.enabled ? (
            <EmptyState title="사용하지 않는 기준입니다." className="py-16" />
          ) : !(customerData?.items || []).length ? (
            <EmptyState title="현재 기준에 해당하는 고객이 없습니다." className="py-16" />
          ) : (
            <div className="divide-y divide-sgez-border">
              {(customerData.items || []).map((item) => (
                <CustomerRow key={item.customerRefId} item={item} isCoverage={activeRule?.category === 'COVERAGE'} />
              ))}
            </div>
          )}

          {(customerData?.totalPages || 0) > 1 ? (
            <div className="flex items-center justify-between border-t border-sgez-border px-4 py-3">
              <button
                type="button"
                aria-label="이전 페이지"
                disabled={page <= 0 || customerLoading}
                onClick={() => setPage((value) => Math.max(0, value - 1))}
                className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-sgez-border-strong text-slate-700 disabled:opacity-40"
              >
                <ChevronLeft className="h-4 w-4" aria-hidden="true" />
              </button>
              <span className="text-xs font-semibold tabular-nums text-slate-600">
                {page + 1} / {customerData.totalPages}
              </span>
              <button
                type="button"
                aria-label="다음 페이지"
                disabled={page + 1 >= customerData.totalPages || customerLoading}
                onClick={() => setPage((value) => value + 1)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-sgez-border-strong text-slate-700 disabled:opacity-40"
              >
                <ChevronRight className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          ) : null}
        </section>
      </div>
      )}

      {settingsOpen ? (
        <WorkHubSettingsDialog
          categories={draftCategories}
          activeCategory={settingsCategory}
          onCategoryChange={setSettingsCategory}
          onChange={setDraftCategories}
          onClose={() => setSettingsOpen(false)}
          onSave={saveSettings}
          saving={saving}
          error={saveError}
        />
      ) : null}
    </PageFrame>
  )
}

function WeeklyRecommendationSection({ data, loading, error, activeTopicId, customerData, page, onSelect, onPageChange, onRetry }) {
  const topics = Array.isArray(data?.topics) ? data.topics : []
  const activeTopic = topics.find((topic) => topic.id === activeTopicId) || topics[0]
  const items = Array.isArray(customerData?.items) ? customerData.items : []
  const totalPages = Number(customerData?.totalPages || 0)

  return (
    <div className="grid min-h-[540px] gap-4 lg:grid-cols-[340px_minmax(0,1fr)]">
      <section className="overflow-hidden border border-sgez-border-strong bg-white">
        <div className="flex h-12 items-center border-b border-sgez-border bg-sgez-surface-subtle px-4">
          <h2 className="text-sm font-bold text-slate-900">추천 주제 이력</h2>
        </div>
        {loading && !topics.length ? (
          <div className="px-4 py-12 text-center text-sm text-slate-500">불러오는 중...</div>
        ) : topics.length ? (
          <div className="divide-y divide-sgez-border">
            {topics.map((topic) => {
              const selected = topic.id === activeTopic?.id
              return (
                <button key={topic.id} type="button" onClick={() => onSelect(topic.id)} className={`flex w-full items-center gap-3 px-4 py-3 text-left transition ${selected ? 'bg-sky-50' : 'hover:bg-slate-50'}`}>
                  <span className="min-w-0 flex-1">
                    <span className={`block truncate text-sm font-bold ${selected ? 'text-brand-navy' : 'text-slate-800'}`}>{topic.title}</span>
                    <span className="mt-0.5 block text-xs text-slate-500">{topic.startsOn} ~ {topic.endsOn}</span>
                  </span>
                  <span className="min-w-10 text-right text-lg font-black tabular-nums text-slate-900">{topic.total || 0}</span>
                  <ChevronRight className={`h-4 w-4 shrink-0 ${selected ? 'text-brand-sky' : 'text-slate-300'}`} aria-hidden="true" />
                </button>
              )
            })}
          </div>
        ) : <EmptyState title="현재 추천 주제가 없습니다." className="py-12" />}
      </section>

      <section className="min-w-0 border border-sgez-border-strong bg-white">
        <div className="flex min-h-16 flex-wrap items-center justify-between gap-3 border-b border-sgez-border bg-sgez-surface-subtle px-4 py-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="truncate text-base font-black text-slate-900">{activeTopic?.title || '추천 고객'}</h2>
              {activeTopic ? <span className="text-sm font-bold tabular-nums text-brand-sky">{customerData?.total ?? activeTopic.total ?? 0}명</span> : null}
            </div>
            {activeTopic ? <div className="mt-0.5 text-xs text-slate-500">{activeTopic.startsOn} ~ {activeTopic.endsOn}</div> : null}
          </div>
        </div>
        {activeTopic?.summary ? <div className="border-b border-sgez-border px-4 py-2 text-sm text-slate-600">{activeTopic.summary}</div> : null}
        {error ? <ErrorState description={error} onAction={onRetry} className="py-10" /> : loading ? <div className="px-4 py-16 text-center text-sm text-slate-500">추천 고객을 확인하는 중...</div> : !activeTopic ? <EmptyState title="현재 추천 주제가 없습니다." description="곧 추천 주제가 추가될 예정입니다." className="py-16" /> : items.length ? <div className="divide-y divide-sgez-border">{items.map((item) => <CustomerRow key={item.customerRefId} item={item} />)}</div> : <EmptyState title="현재 추천 기준에 해당하는 고객이 없습니다." className="py-16" />}
        {totalPages > 1 ? <div className="flex items-center justify-between border-t border-sgez-border px-4 py-3"><button type="button" aria-label="이전 추천 고객 페이지" disabled={page <= 0 || loading} onClick={() => onPageChange(Math.max(0, page - 1))} className="inline-flex h-9 w-9 items-center justify-center rounded border border-sgez-border-strong disabled:opacity-40"><ChevronLeft className="h-4 w-4" /></button><span className="text-xs font-semibold tabular-nums text-slate-600">{page + 1} / {totalPages}</span><button type="button" aria-label="다음 추천 고객 페이지" disabled={page + 1 >= totalPages || loading} onClick={() => onPageChange(page + 1)} className="inline-flex h-9 w-9 items-center justify-center rounded border border-sgez-border-strong disabled:opacity-40"><ChevronRight className="h-4 w-4" /></button></div> : null}
      </section>
    </div>
  )
}
function CustomerRow({ item, isCoverage = false }) {
  return (
    <div className="px-4 py-3 sm:px-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="font-bold text-slate-900">{item.customerName || '이름 미등록'}</span>
            {item.customerPhone ? <span className="text-xs text-slate-500">{item.customerPhone}</span> : null}
            {item.signalLabel ? (
              <span className="rounded-sm bg-slate-100 px-1.5 py-0.5 text-[11px] font-semibold text-slate-600">
                {item.signalLabel}
              </span>
            ) : null}
          </div>
          {!isCoverage && item.reason ? <p className="mt-1 text-sm leading-5 text-slate-700">{item.reason}</p> : null}
          {!isCoverage && Array.isArray(item.breakdown) && item.breakdown.length ? (
            <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500">
              {item.breakdown.map((part) => (
                <span key={part.label}>{part.label} {money(part.amount)}</span>
              ))}
            </div>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Link
            to={item.targetPath || `/support/customers/${item.customerRefId}`}
            className="inline-flex h-9 items-center rounded-md border border-sgez-border-strong bg-white px-3 text-xs font-bold text-slate-700 hover:bg-slate-50"
          >
            상세
          </Link>
          <Link
            to={item.contactPath || `/support/customers/${item.customerRefId}#contact`}
            className="inline-flex h-9 items-center rounded-md bg-brand-navy px-3 text-xs font-bold text-white hover:opacity-90"
          >
            연락 기록
          </Link>
        </div>
      </div>
    </div>
  )
}

function WorkHubSettingsDialog({
  categories,
  activeCategory,
  onCategoryChange,
  onChange,
  onClose,
  onSave,
  saving,
  error,
}) {
  const category = categories.find((item) => item.key === activeCategory) || categories[0]

  const updateRule = (ruleCode, updater) => {
    onChange(categories.map((item) => ({
      ...item,
      rules: (item.rules || []).map((rule) => (rule.code === ruleCode ? updater(rule) : rule)),
    })))
  }

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-950/45 p-3 sm:p-6" role="presentation">
      <div className="flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden border border-sgez-border-strong bg-white shadow-2xl" role="dialog" aria-modal="true" aria-labelledby="work-hub-settings-title">
        <div className="flex h-14 shrink-0 items-center justify-between border-b border-sgez-border px-4 sm:px-5">
          <h2 id="work-hub-settings-title" className="text-base font-black text-slate-900">고객업무 기준 설정</h2>
          <button type="button" onClick={onClose} aria-label="닫기" className="inline-flex h-9 w-9 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100">
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <div className="flex shrink-0 overflow-x-auto border-b border-sgez-border px-4" role="tablist" aria-label="설정 카테고리">
          {categories.map((item) => (
            <button
              key={item.key}
              type="button"
              role="tab"
              aria-selected={item.key === activeCategory}
              onClick={() => onCategoryChange(item.key)}
              className={`min-w-[96px] border-b-2 px-4 py-3 text-sm font-bold ${
                item.key === activeCategory ? 'border-brand-navy text-brand-navy' : 'border-transparent text-slate-500'
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="divide-y divide-sgez-border">
            {(category?.rules || []).map((rule) => (
              <RuleSettings
                key={rule.code}
                rule={rule}
                onChange={(next) => updateRule(rule.code, () => next)}
              />
            ))}
          </div>
        </div>

        <div className="shrink-0 border-t border-sgez-border bg-sgez-surface-subtle px-4 py-3 sm:px-5">
          {error ? <div className="mb-2 text-sm font-medium text-rose-700">{error}</div> : null}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} disabled={saving} className="h-10 rounded-md border border-sgez-border-strong bg-white px-4 text-sm font-bold text-slate-700 disabled:opacity-50">
              취소
            </button>
            <button type="button" onClick={onSave} disabled={saving} className="inline-flex h-10 items-center gap-2 rounded-md bg-brand-navy px-4 text-sm font-bold text-white disabled:opacity-50">
              <Check className="h-4 w-4" aria-hidden="true" />
              {saving ? '저장 중...' : '저장'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function RuleSettings({ rule, onChange }) {
  const config = rule.config || {}
  const setConfig = (patch) => onChange({ ...rule, config: { ...config, ...patch } })
  const thresholdManwon = Math.round(Number(config.thresholdAmount || 0) / 10000)

  return (
    <section className={`px-4 py-2.5 sm:px-5 ${rule.enabled ? 'bg-white' : 'bg-slate-50'}`}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <button
          type="button"
          role="switch"
          aria-checked={Boolean(rule.enabled)}
          onClick={() => onChange({ ...rule, enabled: !rule.enabled })}
          className={`relative h-6 w-11 shrink-0 rounded-full transition ${rule.enabled ? 'bg-slate-700' : 'bg-slate-300'}`}
        >
          <span className={`absolute top-1 h-4 w-4 rounded-full bg-white transition ${rule.enabled ? 'left-6' : 'left-1'}`} />
        </button>
        <div className="flex min-w-[220px] flex-1 flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span className="text-sm font-black text-slate-900">{rule.label}</span>
          <span className="text-xs text-slate-500">{rule.description}</span>
        </div>

      {rule.enabled ? (
        <div className={`ml-auto flex flex-wrap items-center gap-2 ${Array.isArray(rule.options) && rule.options.length ? 'w-full sm:ml-14 lg:ml-auto lg:w-[360px]' : ''}` }>
          {Object.prototype.hasOwnProperty.call(config, 'days') ? (
            <InlineNumberField
              label={rule.code === 'OTHER_MATURITY_RENEWAL' ? '앞으로' : '기간'}
              value={config.days}
              min={1}
              max={3650}
              suffix="일"
              onChange={(value) => setConfig({ days: value })}
            />
          ) : null}

          {Object.prototype.hasOwnProperty.call(config, 'leadDays') ? (
            <InlineNumberField
              label="앞으로"
              value={config.leadDays}
              min={0}
              max={365}
              suffix="일"
              onChange={(value) => setConfig({ leadDays: value })}
            />
          ) : null}

          {Object.prototype.hasOwnProperty.call(config, 'includeFamily') ? (
            <label className="inline-flex h-8 items-center gap-1.5 whitespace-nowrap text-xs font-semibold text-slate-700">
              <input
                type="checkbox"
                checked={Boolean(config.includeFamily)}
                onChange={(event) => setConfig({ includeFamily: event.target.checked })}
                className="h-4 w-4 rounded border-slate-300 accent-slate-600 focus:ring-slate-400"
              />
              가족 생일 포함
            </label>
          ) : null}

                    {Object.prototype.hasOwnProperty.call(config, 'regionCode') ? (
            <label className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-bold text-slate-600">
              <span>지역</span>
              <select
                value={config.regionCode || 'SEOUL'}
                onChange={(event) => setConfig({ regionCode: event.target.value })}
                className="h-8 w-[160px] rounded-md border border-sgez-border-strong bg-white px-2 text-xs text-slate-800"
              >
                {(rule.regionOptions || []).map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
          ) : null}
{Array.isArray(rule.options) && rule.options.length ? (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <label className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-bold text-slate-600">
                  <span>비교 기준</span>
                  <select
                    value={config.comparison || 'ANY'}
                    onChange={(event) => setConfig({ comparison: event.target.value })}
                    className="h-8 w-[148px] rounded-md border border-sgez-border-strong bg-white px-2 text-xs text-slate-800"
                  >
                    {(rule.comparisonOptions || []).map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </label>
                {config.comparison !== 'ANY' ? (
                  <InlineNumberField
                    label={rule.metricType === 'MONTHLY_PREMIUM' ? '월보험료 기준' : '가입금액 기준'}
                    value={thresholdManwon}
                    min={0}
                    max={10000000}
                    suffix="만원"
                    onChange={(value) => setConfig({ thresholdAmount: value * 10000 })}
                  />
                ) : null}
              </div>
              <div className="basis-full flex items-start gap-3 pt-1">
                <div className="shrink-0 pt-1 text-xs font-bold text-slate-600">포함 담보</div>
                <div className="flex min-w-0 flex-1 flex-wrap gap-1.5">
                  {rule.options.map((option) => {
                    const selected = Array.isArray(config.selectedCodes) && config.selectedCodes.includes(option.code)
                    return (
                      <label key={option.code} className={`inline-flex h-7 items-center gap-1.5 rounded-md border px-2 text-xs ${selected ? 'border-slate-300 bg-slate-100 text-slate-800' : 'border-sgez-border bg-white text-slate-600'}`}>
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={(event) => {
                            const current = Array.isArray(config.selectedCodes) ? config.selectedCodes : []
                            const next = event.target.checked
                              ? [...new Set([...current, option.code])]
                              : current.filter((code) => code !== option.code)
                            setConfig({ selectedCodes: next })
                          }}
                          className="h-4 w-4 shrink-0 rounded border-slate-300 accent-slate-600 focus:ring-slate-400"
                        />
                        <span className="whitespace-nowrap">{option.label}</span>
                      </label>
                    )
                  })}
                </div>
              </div>
            </>
          ) : null}
        </div>
      ) : null}
      </div>
    </section>
  )
}

function InlineNumberField({ label, value, min, max, suffix, onChange }) {
  return (
    <label className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-bold text-slate-600">
      <span>{label}</span>
      <span className="flex h-8 w-[116px] overflow-hidden rounded-md border border-sgez-border-strong bg-white focus-within:border-brand-sky focus-within:ring-1 focus-within:ring-brand-sky/30">
        <input
          type="number"
          inputMode="numeric"
          min={min}
          max={max}
          value={value ?? ''}
          onChange={(event) => onChange(Number(event.target.value || 0))}
          className="min-w-0 flex-1 border-0 px-2 text-right text-xs tabular-nums outline-none"
        />
        <span className="inline-flex items-center border-l border-sgez-border bg-slate-50 px-2 text-[11px] font-semibold text-slate-600">{suffix}</span>
      </span>
    </label>
  )
}
